import React, { lazy, Suspense, useEffect, useCallback } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { HeroSection } from "@/components/home/HeroSection";
import { FeatureCards } from "@/components/home/FeatureCards";
import { preloadCriticalImages, preloadVisibleImages } from "@/utils/imageOptimizer";
import { NewsletterSection } from "@/components/home/NewsletterSection";
import { StatisticsSection } from "@/components/home/StatisticsSection";
import { MedicationHomeSection } from "@/components/home/MedicationHomeSection";
import { AppointmentSection } from "@/components/home/AppointmentSection";
import { JournalSection } from "@/components/home/JournalSection";
import { AiAssistanceSection } from "@/components/home/AiAssistanceSection";
import { GestationTrackingSection } from "@/components/home/GestationTrackingSection";
import { MobileGestationTrackingSection } from "@/pages/mobile/MobileGestationTrackingSection";
import MobileAiAssistanceSection from "@/pages/mobile/MobileAiAssistanceSection";
import { MobileMedicationManagementSection } from "@/components/home/MobileMedicationManagementSection";
import { MobileAppointmentSection } from "@/pages/mobile/MobileAppointmentSection";
import { DonationSection } from "@/components/home/DonationSection";
import { MobileDonationSection } from "@/pages/mobile/MobileDonationSection";
import { MobileAiMedicalSection } from "@/components/home/MobileAiMedicalSection";
import { PageBackground } from "@/components/ui/page-background";
import { SEOHead } from "@/components/seo/SEOHead";
import { StructuredData } from "@/components/seo/StructuredData";

const LoadingFallback = () => (
  <div className="h-64 animate-pulse bg-muted rounded-lg" />
);

const Index = () => {
  const isMobile = useIsMobile();
  
  const setupIntersectionObserver = useCallback(() => {
    if (typeof window === 'undefined' || !window.IntersectionObserver) return;
    
    const observerOptions = {
      root: null,
      rootMargin: '200px',
      threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const section = entry.target as HTMLElement;
          
          section.dataset.visible = 'true';
          
          observer.unobserve(entry.target);
          
          const lazyImages = section.querySelectorAll('img[data-src]');
          if (lazyImages.length > 0) {
            lazyImages.forEach(img => {
              const imgElement = img as HTMLImageElement;
              if (imgElement.dataset.src) {
                preloadCriticalImages([imgElement.dataset.src]);
              }
            });
          }
        }
      });
    }, observerOptions);
    
    document.querySelectorAll('.content-visibility-auto').forEach(section => {
      observer.observe(section);
    });
    
    return () => {
      observer.disconnect();
    };
  }, []);
  
  useEffect(() => {
    preloadCriticalImages([
      "/lovable-uploads/fd2d860d-2773-4096-9f0d-d73e6ddb0d73.png",
      "/lovable-uploads/8efb7f8c-1bb7-4a0e-b5f4-616efc7afd07.png"
    ]);
    
    const cleanupObserver = setupIntersectionObserver();
    
    try {
      // Add try-catch block to handle potential errors with preloadVisibleImages
      preloadVisibleImages(".content-visibility-auto", "img[data-src]");
      console.log("Image preloading initiated successfully");
    } catch (error) {
      console.error("Error during image preloading:", error);
    }
    
    if ('contentVisibility' in document.documentElement.style) {
      document.querySelectorAll('.content-visibility-auto').forEach(section => {
        (section as HTMLElement).style.contentVisibility = 'auto';
        (section as HTMLElement).style.containIntrinsicSize = '0 500px';
      });
    }
    
    return cleanupObserver;
  }, [setupIntersectionObserver]);
  
  if (isMobile) {
    return (
      <>
        <SEOHead 
          title="Médicace - Votre Santé Mobile"
          description="Accédez à tous vos services de santé depuis votre mobile. Téléconsultations, suivi médical et IA médicale à portée de main."
          keywords="santé mobile, app santé, téléconsultation mobile, suivi médical mobile"
        />
        <StructuredData type="organization" />
        <div className="space-y-8 py-4">
          <MobileAiMedicalSection />
          <MobileAppointmentSection />
          <MobileMedicationManagementSection />
          <MobileGestationTrackingSection />
          <MobileDonationSection />
        </div>
      </>
    );
  }
  
  return (
    <>
      <SEOHead 
        title="Médicace - Plateforme de Santé Intelligente"
        description="Découvrez Médicace, votre plateforme de santé digitale complète. Téléconsultations, suivi médical, rappels médicaments, IA médicale et bien plus pour une santé optimisée."
        keywords="médicace, santé digitale, téléconsultation, plateforme médicale, suivi santé, IA médicale, carnet santé numérique"
        type="website"
      />
      <StructuredData type="organization" />
      <StructuredData type="breadcrumb" />
      
      <div className="relative container mx-auto px-4 py-2">
        <PageBackground variant="default" intensity="medium" />
        
        <HeroSection />
        <FeatureCards />
        <section className="content-visibility-auto mt-8">
          <DonationSection />
        </section>
        <section className="content-visibility-auto mt-8">
          <MedicationHomeSection />
        </section>
        <section className="content-visibility-auto mt-8">
          <AppointmentSection />
        </section>
        <section className="content-visibility-auto mt-8">
          <JournalSection />
        </section>
        <section className="content-visibility-auto mt-8">
          <AiAssistanceSection />
        </section>
        <section className="content-visibility-auto mt-8">
          <GestationTrackingSection />
        </section>
        <section className="content-visibility-auto mt-8">
          <StatisticsSection />
        </section>
        <section className="content-visibility-auto mt-8">
          <NewsletterSection />
        </section>
      </div>
    </>
  );
};

export default Index;
